import * as React from "react";
import { CalendarIcon } from "lucide-react";
import { de, enUS } from "react-day-picker/locale";
import { useTranslation } from "react-i18next";

import dayjs from "@/lib/dayjs";
import { Calendar } from "@/components/shadcn/calendar";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
} from "@/components/shadcn/input-group";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/shadcn/popover";

/** Display and input format of the text field. */
const DISPLAY_FORMAT = "DD.MM.YYYY";
/** Format of the `value` prop and of the filter query. */
const VALUE_FORMAT = "YYYY-MM-DD";

interface Props {
  /** Date as `YYYY-MM-DD`. */
  value?: string;
  /** Empty string when the date was cleared. */
  onChange: (newValue: string) => void;
  /** Earliest selectable day as `YYYY-MM-DD`; earlier days are disabled in the calendar. */
  minDate?: string;
  /** Latest selectable day as `YYYY-MM-DD`; later days are disabled in the calendar. */
  maxDate?: string;
}

export function FilterDatePicker({ value, onChange, minDate, maxDate }: Props) {
  const { i18n } = useTranslation("filters");
  const calendarLocale = i18n.resolvedLanguage?.startsWith("de") ? de : enUS;

  const selected = value ? dayjs(value, VALUE_FORMAT, true) : undefined;
  const selectedDate = selected?.isValid() ? selected.toDate() : undefined;

  const min = minDate ? dayjs(minDate, VALUE_FORMAT, true).toDate() : undefined;
  const max = maxDate ? dayjs(maxDate, VALUE_FORMAT, true).toDate() : undefined;
  const disabled = [...(min ? [{ before: min }] : []), ...(max ? [{ after: max }] : [])];

  const [open, setOpen] = React.useState(false);
  const [month, setMonth] = React.useState<Date | undefined>(selectedDate);
  const [text, setText] = React.useState(selected?.isValid() ? selected.format(DISPLAY_FORMAT) : "");

  // Mirror external changes (e.g. a filter reset) back into the text field.
  React.useEffect(() => {
    const next = value ? dayjs(value, VALUE_FORMAT, true) : undefined;
    setText(next?.isValid() ? next.format(DISPLAY_FORMAT) : "");
    setMonth(next?.isValid() ? next.toDate() : undefined);
  }, [value]);

  const handleTextChange = (input: string) => {
    setText(input);
    const typed = dayjs(input.trim(), DISPLAY_FORMAT, true);
    if (typed.isValid()) {
      setMonth(typed.toDate());
      onChange(typed.format(VALUE_FORMAT));
    } else if (input.trim() === "") {
      onChange("");
    }
  };

  const handleSelect = (date: Date | undefined) => {
    if (date) onChange(dayjs(date).format(VALUE_FORMAT));
    setOpen(false);
  };

  return (
    <InputGroup className="h-9 w-40">
      <InputGroupInput
        value={text}
        placeholder={DISPLAY_FORMAT}
        className="text-sm"
        onChange={(e) => handleTextChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "ArrowDown") {
            e.preventDefault();
            setOpen(true);
          }
        }}
      />
      <InputGroupAddon align="inline-end">
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <InputGroupButton variant="ghost" size="icon-xs">
              <CalendarIcon className="h-4 w-4" />
            </InputGroupButton>
          </PopoverTrigger>
          <PopoverContent className="w-auto overflow-hidden p-0" align="end" alignOffset={-8} sideOffset={10}>
            <Calendar
              mode="single"
              locale={calendarLocale}
              selected={selectedDate}
              month={month}
              onMonthChange={setMonth}
              onSelect={handleSelect}
              disabled={disabled}
              startMonth={min}
              endMonth={max}
              autoFocus
            />
          </PopoverContent>
        </Popover>
      </InputGroupAddon>
    </InputGroup>
  );
}
